import React from "react";
import { NavLink } from "react-router-dom";

/**
 * The Check-in tab strip.
 *
 * Two existing portal idioms, joined rather than invented: the markup is the
 * Metronic `nav nav-tabs nav-tabs-line` strip used in ~16 edit screens
 * (`Users/UserEdit.js`, `Batches/Classes/BatchEdit.js`, `Events/EventEdit.js`
 * …), and the active-state handling is the `NavLink` + `activeClassName="active"`
 * that every route-driven nav in the portal uses (`BranchMaintenance/Components/
 * BranchMaintenanceNav.js`, `FamilyProfile/FamilyNav.js`).
 *
 * Those edit screens drive their strips from `useState`, which is right for a
 * form's sections but wrong here: these three are routes, so each one has to be
 * linkable, bookmarkable and reachable with the back button. `NavLink` gets the
 * active styling from the URL, so there is no second copy of "which tab is
 * open" to keep in step with the router.
 *
 * Placement is the other half of the borrowed idiom, and it is not optional.
 * `nav-tabs-line` is drawn for the inside of a white card: a grey link, a thin
 * rule across the card's full width, and a coloured segment of that rule under
 * the open tab. On the page's blue subheader gradient none of that survives, so
 * each of the three screens renders this strip as the first child of its own
 * `card-body`, directly under the `card-header` — the order BatchEdit uses,
 * header (title + toolbar) → tabs → content.
 *
 * The three card bodies carry `pt-0`/`py-0`, so the gap above the strip is
 * `mt-5` here rather than the body's own padding. Doing it that way also keeps
 * `BadgePrintList`'s `py-0` untouched, which the printed badge sheet depends on.
 *
 * `zr-no-print` matters. It is not decoration: `badge-sheet.css` hides
 * `.zr-no-print` while `body.zr-printing-badges` is set, which is exactly when
 * the badge sheet is on screen. Without it this strip would print across the
 * top of the first sheet of badges.
 */
const TABS = [
  { to: "/check-in/badges", label: "Badges" },
  { to: "/check-in/attendance", label: "Attendance" },
  { to: "/check-in/competitions", label: "Competitions" },
];

export function CheckInTabs() {
  return (
    <ul
      className="nav nav-tabs nav-tabs-line mt-5 mb-5 zr-no-print"
      role="tablist"
    >
      {TABS.map((tab) => (
        <li className="nav-item" key={tab.to}>
          <NavLink className="nav-link" activeClassName="active" to={tab.to}>
            {tab.label}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
